/**
 * Claude transport — vendor-specific wire layer for the /api/claude edge
 * function. Matches the pipedrive/hubspot layout under common/.
 *
 * This module is intentionally un-Vue: it has no reactivity, no lifecycle,
 * no stores. Callers compose reactive state around it via useClaude().
 */

import { bearerHeader } from "@/common/authorization"

export interface ChatMessage {
  role: "user" | "assistant"
  content: string
}

export interface NavigateAction {
  url: string
  label?: string
}

export interface StreamOptions {
  signal?: AbortSignal
  onNavigate?: (action: NavigateAction) => void
}

// The SSE events edge-functions/claude.ts sends. Declared here, not shared:
// the edge function is typechecked by Deno and this file is not importable
// there.
type ClaudeStreamEvent =
  | { type: "content_block_delta"; delta?: { type?: string; text?: string } }
  | { type: "navigate"; url?: string; label?: string }

async function buildAiRequestHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: await bearerHeader(),
    "X-Requested-With": "XMLHttpRequest",
  }
}

/**
 * Stream a message to the Claude API. Calls onDelta with each text chunk
 * as it arrives. Calls options.onNavigate when the AI wants to navigate
 * the user to a page. Returns a promise that resolves when the stream is
 * complete, or rejects on error.
 */
export async function streamMessage(
  system: string,
  messages: ChatMessage[],
  onDelta: (text: string) => void,
  options: StreamOptions = {}
): Promise<void> {
  const { signal, onNavigate } = options
  return fetch("/api/claude", {
    method: "POST",
    headers: await buildAiRequestHeaders(),
    body: JSON.stringify({
      system,
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
    }),
    signal,
  }).then((response) => {
    if (!response.ok) {
      return response.text().then((text) => {
        throw new Error(text || `AI request failed (${response.status})`)
      })
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error("No response stream")

    const decoder = new TextDecoder()
    let buffer = ""

    const processStream = () =>
      reader.read().then(({ done, value }): Promise<void> | undefined => {
        if (done) return

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() || ""

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue
          const data = line.slice(6)
          if (data === "[DONE]") return

          try {
            const event = JSON.parse(data) as ClaudeStreamEvent
            if (event.type === "content_block_delta" && event.delta?.text) {
              onDelta(event.delta.text)
            } else if (event.type === "navigate" && event.url && onNavigate) {
              onNavigate({ url: event.url, label: event.label })
            }
          } catch {
            // Skip unparseable SSE lines
          }
        }

        return processStream()
      })

    return processStream()
  })
}
